import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

type SentryClient = {
  setTag?: (key: string, value: string) => void;
  setUser?: (user: { id?: string; email?: string } | null) => void;
  onLoad?: (callback: () => void) => void;
  captureException?: (error: unknown) => void;
};

declare global {
  interface Window {
    Sentry?: SentryClient;
  }
}

function withSentry(run: (sentry: SentryClient) => void) {
  try {
    if (typeof window === 'undefined') {
      return;
    }
    const sentry = window.Sentry;
    if (sentry) {
      run(sentry);
    }
  } catch {
    // Loader stub is not the full SDK yet; never crash the app for telemetry.
  }
}

function applyRestaurantTag(pathname: string) {
  withSentry((sentry) => {
    if (typeof sentry.setTag !== 'function') {
      return;
    }
    const restaurantMatch = pathname.match(/^\/restaurants\/([^/]+)/);
    if (restaurantMatch?.[1]) {
      sentry.setTag('restaurant_slug', restaurantMatch[1]);
    }
  });
}

/** Enriches Sentry events with route context after the CDN loader initializes. */
function SentryBootstrap() {
  const location = useLocation();

  useEffect(() => {
    applyRestaurantTag(location.pathname);

    let cancelled = false;
    withSentry((sentry) => {
      if (typeof sentry.setTag === 'function' || typeof sentry.onLoad !== 'function') {
        return;
      }
      sentry.onLoad(() => {
        if (!cancelled) {
          applyRestaurantTag(location.pathname);
        }
      });
    });

    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  return null;
}

export default SentryBootstrap;
